import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import gradeService from "../../services/gradeService";
import ConfigManager from "../../utils/configManager";

// Only noisy when REACT_APP_ENABLE_API_LOGGING is on - the notice is easy to miss otherwise
// and there is no other trace of why it decided to stay silent.
const debug = (...args) => {
  if (ConfigManager.isLoggingEnabled()) {
    console.info("[RejectionNotice]", ...args);
  }
};

// Shown once per login - the Revisor rejection used to be mailed out, now the learner is told
// about it the next time they are in Journey, and again on every following login until the
// semester is corrected. Dismissing only silences it for the current login: the flag lives in
// sessionStorage (gone with the tab) and is cleared on login/logout on top of that, so a
// second session in the same tab starts over.
const KEY_PREFIX = "journey.rejectionNotice.seen.";
const sessionKeyFor = (userId) => `${KEY_PREFIX}${userId ?? "unknown"}`;

export const clearRejectionNoticeDismissals = () => {
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith(KEY_PREFIX))
    .forEach((key) => sessionStorage.removeItem(key));
};

// Tolerant against the role shapes floating around ("LERNENDE", { name }, { authority },
// with or without the ROLE_ prefix) - same normalisation as MyBonusPage/GradeRouter.
const roleName = (role) => {
  const raw = typeof role === "string" ? role : role?.name || role?.authority;
  return (raw || "").toUpperCase().replace(/^ROLE_/, "");
};

const isLearner = (user) =>
  (user?.roles || []).some((role) => {
    const name = roleName(role);
    return name === "LERNENDE" || name === "STUDENT";
  });

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const RejectionNoticeModal = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [rejections, setRejections] = useState([]);

  const userId = user?.id;
  const learner = isLearner(user);

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated || !userId) {
      debug("skipped: not authenticated yet", { isAuthenticated, userId });
      return undefined;
    }
    // Onboarding is a hard redirect in ProtectedRoute, so there is no point showing a notice
    // over it. forcePasswordReset is deliberately NOT a reason to skip: it only drives a
    // reminder in the navigation and stays set on accounts that keep using Journey normally.
    if (user?.onboardingRequired) {
      debug("skipped: onboarding pending");
      return undefined;
    }
    // The endpoint is learner-only and answers everyone else with a 403, which the global
    // axios error logger prints on every page. Nothing to show for them anyway, so don't ask.
    if (!learner) {
      debug("skipped: not a learner");
      return undefined;
    }

    const check = async () => {
      if (sessionStorage.getItem(sessionKeyFor(userId)) === "true") {
        debug("skipped: already dismissed in this session");
        return;
      }
      const pending = await gradeService.getMyPendingRejections();
      debug("pending rejections:", pending);
      if (cancelled || !Array.isArray(pending)) return;
      setRejections(pending);
    };

    check();

    // A Revisor can send a semester back while the learner already has Journey open, so
    // re-check whenever the tab becomes visible again instead of only on page load.
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isAuthenticated, userId, learner, user?.onboardingRequired]);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(sessionKeyFor(userId), "true");
    setRejections([]);
  }, [userId]);

  const openSemester = useCallback(
    (rejection) => {
      dismiss();
      const params = new URLSearchParams({ semesterId: String(rejection.semesterId) });
      if (rejection.classId) params.set("classId", String(rejection.classId));
      navigate(`/grades?${params.toString()}`);
    },
    [dismiss, navigate]
  );

  if (rejections.length === 0) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
      style={{ zIndex: 80 }}
    >
      <div className="bg-white rounded-2xl shadow-brand-xl border border-pale-dawn w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3 flex-shrink-0">
              <i className="fas fa-undo text-red-600 text-lg"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900">
              Your semester grades were sent back
            </h3>
          </div>

          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The Revisor reviewed your definitive grades / report and sent them back for
            correction. Please correct them and confirm the semester again.
          </p>

          <div className="space-y-3 mb-6">
            {rejections.map((rejection) => (
              <div
                key={`${rejection.semesterId}-${rejection.classId ?? "legacy"}`}
                className="border border-red-200 bg-red-50 rounded-lg p-4"
              >
                <div className="flex items-center justify-between gap-3 mb-1">
                  <span className="font-semibold text-red-800 text-sm">
                    {rejection.semesterName}
                  </span>
                  {rejection.rejectedAt && (
                    <span className="text-xs text-red-600">
                      {formatDate(rejection.rejectedAt)}
                    </span>
                  )}
                </div>
                {rejection.rejectionReason && (
                  <p className="text-sm text-red-700 whitespace-pre-line">
                    <span className="font-medium">Reason: </span>
                    {rejection.rejectionReason}
                  </p>
                )}
                <button
                  onClick={() => openSemester(rejection)}
                  className="mt-3 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors inline-flex items-center"
                >
                  <i className="fas fa-arrow-right mr-2"></i>
                  Go to semester
                </button>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <button
              onClick={dismiss}
              className="px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectionNoticeModal;
